import { useState } from "react";
import { useUsageLimit } from "../../../customhooks/useUsageLimit.js";
import { useAuth } from "../../Login/AuthContext.js";
import { CircularProgress } from "@mui/material";
import glitterIcon from "../../../assets/glitter.svg";
import numbersIcon from "../../../assets/numbers.png";
import operationIcon from "../../../assets/math-operations.svg";
import "./PictureOperations.css";
import PromptPreview from "../../CustomPrompt/PromptPreview.jsx";

// Toggle which parts of the worksheet config are shown in the preview box
const PROMPT_PARTS = {
  template: true,         // Template name
  questionSettings: true, // Number of questions, operation type, number range
  mediaSelection: true,   // Selected emojis
};

function buildPromptPreview(selectedEmojis, operationType, numberRange) {
  const lines = [];
  if (PROMPT_PARTS.template)
    lines.push('Template: Number Operations Worksheet');
  if (PROMPT_PARTS.questionSettings) {
    lines.push(`Questions: 5`);
    lines.push(`Operation: ${operationType || '[not set]'}`);
    lines.push(`Number range: ${numberRange}`);
  }
  if (PROMPT_PARTS.mediaSelection)
    lines.push(`Emojis: ${selectedEmojis.length > 0 ? selectedEmojis.join(' ') : '[none selected]'}`);
  return lines.join('\n');
}

const emojiSet = [
  "🍎", "🐶", "🚗", "🌸", "⭐", "⚽",
  "🍌", "🐠", "🎈", "🦋", "🍓", "🐸",
  "🦄", "🚀", "🍕", "🐧", "🎵", "🍩"
];

const NumberOperationsTemplate = ({ generateContentAndPdf, loading }) => {
  const [selectedEmojis, setSelectedEmojis] = useState([]);
  const [operationType, setOperationType] = useState("mixed");
  const [numberRange, setNumberRange] = useState("1-10");

  const promptPreview = buildPromptPreview(selectedEmojis, operationType, numberRange);

  const { isAuthenticated } = useAuth();
  const { remainingUses } = useUsageLimit(
    "globalUsage",
    5,
    undefined,
    isAuthenticated
  );

  const [errors, setErrors] = useState({
    emojiError: "",
    operationError: "",
    rangeError: "",
  });

  const toggleEmoji = (emoji) => {
    let updated;
    if (selectedEmojis.includes(emoji)) {
      updated = selectedEmojis.filter((e) => e !== emoji);
    } else {
      updated = [...selectedEmojis, emoji];
    }
    if (updated.length <= 6) {
      setSelectedEmojis(updated);
      if (updated.length > 0) {
        setErrors((prev) => ({ ...prev, emojiError: "" }));
      }
    }
  };

  const submitHandler = (e) => {
    e.preventDefault();
    let hasError = false;
    const newErrors = {};

    if (selectedEmojis.length === 0) {
      newErrors.emojiError = "Please select at least one emoji.";
      hasError = true;
    }
    if (!operationType) {
      newErrors.operationError = "Please choose operation type.";
      hasError = true;
    }
    if (!numberRange) {
      newErrors.rangeError = "Please choose number range.";
      hasError = true;
    }

    if (hasError) {
      setErrors(newErrors);
      return;
    }

    setErrors({});

    const payload = {
      template: "picture-ops-template",
      worksheetName: "Number Operations Worksheet",
      options: {
        numQuestions: 5,
        selectedEmojis: selectedEmojis,
        operationType: operationType,
        numberRange: numberRange,
      },
    };

    generateContentAndPdf(payload);
  };

  return (
    <div className="number-operations-template column-questions">


      <PromptPreview text={promptPreview} />

      {/* Emoji picker — 6 columns = 3 rows */}
      <div className="input-group">
        <label className="form-label">
          Select emojis for your operations (choose 2–4 for best variety):
        </label>
        <div className="emoji-grid">
          {emojiSet.map((emoji) => (
            <button
              key={emoji}
              type="button"
              className={`emoji-btn ${selectedEmojis.includes(emoji) ? "selected" : ""}`}
              onClick={() => toggleEmoji(emoji)}
            >
              {emoji}
            </button>
          ))}
        </div>
        <small className="emoji-count">Selected: {selectedEmojis.length}/6 emojis</small>
        {errors.emojiError && <p className="error">{errors.emojiError}</p>}
      </div>

      {/* Params row: controls left, generate button right */}
      <div className="po-params-row">
        <div className="po-param">
          <label className="form-label">
            <img src={operationIcon} alt="info" style={{ width: "16px", height: "16px" }} />
            Operation
          </label>
          <select
            className="operation-dropdown"
            value={operationType || ""}
            onChange={(e) => { setOperationType(e.target.value); setErrors((prev) => ({ ...prev, operationError: "" })); }}
          >
            <option value="" disabled>Select Operation</option>
            <option value="addition">Addition</option>
            <option value="subtraction">Subtraction</option>
            <option value="mixed">Mixed (Addition & Subtraction)</option>
          </select>
          {errors.operationError && <p className="error">{errors.operationError}</p>}
        </div>

        <div className="po-param">
          <label className="form-label">
            <img src={numbersIcon} alt="info" style={{ width: "16px", height: "16px" }} />
            Number Range
          </label>
          <select
            className="operation-dropdown"
            value={numberRange}
            onChange={(e) => { setNumberRange(e.target.value); setErrors((prev) => ({ ...prev, rangeError: "" })); }}
          >
            <option value="1-5">1 to 5 (Beginner)</option>
            <option value="1-10">1 to 10 (Intermediate)</option>
            <option value="1-15">1 to 15 (Advanced)</option>
          </select>
          {errors.rangeError && <p className="error">{errors.rangeError}</p>}
        </div>

        <button
          data-gtm-event="track-click"
          gtm-data-label="generated-picture-operations-temp"
          className="generate-button"
          onClick={submitHandler}
          disabled={loading}
        >
          {loading ? (
            <CircularProgress size={18} color="inherit" style={{ marginRight: "8px" }} />
          ) : (
            <img src={glitterIcon} alt="sparkle" style={{ width: "16px", height: "16px", marginRight: "5px" }} />
          )}
          {loading ? "Generating..." : isAuthenticated ? "Generate Questions" : `Generate (${remainingUses} remaining)`}
        </button>
      </div>

      
    </div>
  );
};

export default NumberOperationsTemplate;
